import { useEffect, useState } from 'react';
import type { PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import type { DrillSpec } from '@4cm/core/drills/types';
import { supabase } from '../supabase';
import { loadOfflineDrills } from '../offlineDrills';
import type { LoadState } from '../loadState';

export interface LibraryDrill {
  id: string;
  name: string | null;
  description: string | null;
  drill_type: string | null;
  primary_phase: PhaseOfPlay | null;
  sub_themes: string[];
  duration_mins: number | null;
  source_type: 'PLATFORM' | 'LICENSED' | 'CLUB' | 'AI_GENERATED' | 'CURATED_EXPERT' | null;
  curator_name: string | null;
  curator_credentials: string | null;
  /** Canvas JSON — when present, the card can render a thumbnail
   *  preview and the detail page can render the full canvas. */
  drill_spec: DrillSpec | null;
  /** Audit columns — used by the library page for sort + filter. */
  created_at: string | null;
  updated_at: string | null;
  created_by: string | null;
  /** Display name of the coach who authored a CLUB drill, joined via
   *  users.name. Null for non-club sources. */
  author_name: string | null;
}

interface DrillRow {
  id: string;
  name: string | null;
  description: string | null;
  drill_type: string | null;
  primary_phase: string | null;
  sub_themes: string[] | null;
  duration_mins: number | null;
  source_type: string | null;
  curator_name: string | null;
  curator_credentials: string | null;
  drill_spec: DrillSpec | null;
  created_at: string | null;
  updated_at: string | null;
  created_by: string | null;
  // Supabase nested join: `users(name)` returns the user row or null.
  users?: { name: string | null } | null;
}

const VALID_PHASES: PhaseOfPlay[] = ['attacking', 'defending', 'transitions', 'individual'];

interface UseDrillLibraryOptions {
  /** Single phase filter. Mutually exclusive with `phases` — when both
   *  are provided, `phases` wins. */
  phase?: PhaseOfPlay | null;
  /** Multi-phase filter — used by the grassroots tier where a single
   *  grassroots phase (e.g. in_possession) maps to >1 academy phases
   *  (attacking + individual). Empty array or null = no filter. */
  phases?: readonly PhaseOfPlay[] | null;
  curatedOnly?: boolean;
  limit?: number;
  /** Sort order. 'recent' = newest first, 'name' = A-Z. */
  sort?: 'recent' | 'name';
}

/** Where the rendered drills came from. Pages can use this to add a small
 *  "showing the offline starter library" hint above the list. */
export type DrillSource = 'live' | 'seed';

interface DrillLibraryResult {
  drills: LibraryDrill[] | null;
  state: LoadState;
  source: DrillSource;
}

export function useDrillLibrary(opts: UseDrillLibraryOptions = {}): DrillLibraryResult {
  const { phase = null, phases = null, curatedOnly = false, limit = 60, sort = 'recent' } = opts;
  // Resolve to the effective phase list: `phases` wins when both are set.
  const effectivePhases: PhaseOfPlay[] = phases && phases.length > 0
    ? Array.from(phases)
    : phase
    ? [phase]
    : [];
  const [result, setResult] = useState<DrillLibraryResult>({
    drills: null,
    state: 'loading',
    source: 'live',
  });

  useEffect(() => {
    let cancelled = false;
    setResult({ drills: null, state: 'loading', source: 'live' });
    // Offline seed only filters on a single phase today; for the multi-
    // phase case (grassroots), seed against the first effective phase to
    // get something reasonable + then filter in JS for the rest.
    const seedPhase = effectivePhases[0] ?? null;
    const seed = () => {
      const all = loadOfflineDrills({ phase: seedPhase, curatedOnly, limit });
      const filtered = effectivePhases.length > 1
        ? all.filter((d) => effectivePhases.includes(d.primary_phase as PhaseOfPlay))
        : all;
      return filtered.map((d) => ({
        id: d.id,
        name: d.name,
        description: d.description,
        drill_type: d.drill_type,
        primary_phase: d.primary_phase,
        sub_themes: d.sub_themes,
        duration_mins: d.duration_mins,
        source_type: d.source_type,
        curator_name: d.curator_name,
        curator_credentials: d.curator_credentials,
        drill_spec: null,
        created_at: null,
        updated_at: null,
        created_by: null,
        author_name: null,
      } satisfies LibraryDrill));
    };

    (async () => {
      try {
        let query = supabase
          .from('drills')
          .select('id, name, description, drill_type, primary_phase, sub_themes, duration_mins, source_type, curator_name, curator_credentials, drill_spec, created_at, updated_at, created_by, users:created_by(name)')
          // Hide soft-deleted rows — the canvas + admin tools mark with
          // deleted_at instead of DROP so they're recoverable.
          .is('deleted_at', null)
          .limit(limit);
        if (sort === 'recent') {
          // Recently-updated drills first — coach edits bring the row
          // back to the top, not just the original create.
          query = query.order('updated_at', { ascending: false, nullsFirst: false });
        } else {
          query = query.order('name', { ascending: true });
        }
        if (effectivePhases.length === 1) {
          query = query.eq('primary_phase', effectivePhases[0]);
        } else if (effectivePhases.length > 1) {
          query = query.in('primary_phase', effectivePhases as PhaseOfPlay[]);
        }
        if (curatedOnly) query = query.eq('source_type', 'CURATED_EXPERT');
        const { data, error } = await query;
        if (cancelled) return;
        if (error) {
          console.warn('[drill library] load failed', error.message);
          const fallback = seed();
          setResult({
            drills: fallback,
            state: fallback.length > 0 ? 'ok' : 'empty',
            source: 'seed',
          });
          return;
        }
        // Supabase types the `users:created_by(name)` join as a list
        // when inferred. Cast through unknown — runtime returns a
        // single object for the many-to-one FK relationship.
        const rows = ((data ?? []) as unknown) as DrillRow[];
        if (rows.length > 0) {
          setResult({ drills: rows.map(toLibraryDrill), state: 'ok', source: 'live' });
          return;
        }
        const fallback = seed();
        setResult({
          drills: fallback,
          state: fallback.length > 0 ? 'ok' : 'empty',
          source: 'seed',
        });
      } catch (err) {
        if (cancelled) return;
        console.warn('[drill library] threw', err);
        const fallback = seed();
        setResult({
          drills: fallback,
          state: fallback.length > 0 ? 'ok' : 'empty',
          source: 'seed',
        });
      }
    })();
    return () => { cancelled = true; };
    // effectivePhases is derived from the inputs — re-key by joining for
    // stable string identity so we don't refetch on every parent render.
  }, [phase, phases?.join(','), curatedOnly, limit, sort]);

  return result;
}

function toLibraryDrill(row: DrillRow): LibraryDrill {
  const phase = row.primary_phase as PhaseOfPlay | null;
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    drill_type: row.drill_type,
    primary_phase: phase && VALID_PHASES.includes(phase) ? phase : null,
    sub_themes: row.sub_themes ?? [],
    duration_mins: row.duration_mins,
    source_type: (row.source_type as LibraryDrill['source_type']) ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    created_by: row.created_by,
    author_name: row.users?.name ?? null,
    curator_name: row.curator_name,
    curator_credentials: row.curator_credentials,
    drill_spec: row.drill_spec ?? null,
  };
}
